import { createSlice } from '@reduxjs/toolkit';
import { formatMessageTime } from '../components/whatsapp/chat/conversationTransform';

const getMessageId = (message) => message?.messageId || message?.id;

const moveConversationToTop = (state, conversationId) => {
  const idx = state.conversations.allIds.indexOf(conversationId);
  if (idx > 0) {
    state.conversations.allIds.splice(idx, 1);
    state.conversations.allIds.unshift(conversationId);
  } else if (idx === -1) {
    state.conversations.allIds.unshift(conversationId);
  }
};

const appendMessageIfNew = (messages, message) => {
  if (!Array.isArray(messages)) return [message];
  const msgId = getMessageId(message);
  if (msgId && messages.some((m) => getMessageId(m) === msgId)) {
    return messages;
  }
  return [...messages, message];
};

const bumpConversationOnNewMessage = (
  state,
  conversationId,
  message,
  { incrementUnread = false } = {}
) => {
  if (!conversationId) return;

  const lastMessageAt = message.createdAt || Date.now();
  const previewText = message.text || message.lastMessage || '';
  const time = message.time || formatMessageTime(lastMessageAt);

  const existing = state.conversations.byId[conversationId];
  if (existing) {
    state.conversations.byId[conversationId] = {
      ...existing,
      lastMessage: previewText || existing.lastMessage,
      lastMessageAt,
      time,
      unread: incrementUnread ? (existing.unread || 0) + 1 : existing.unread,
    };
  }

  moveConversationToTop(state, conversationId);

  if (state.currentConversation?.id === conversationId) {
    state.currentConversation.lastMessage = previewText || state.currentConversation.lastMessage;
    state.currentConversation.lastMessageAt = lastMessageAt;
    state.currentConversation.time = time;
  }
};

const initialState = {
  currentConversation: null,
  conversations: { byId: {}, allIds: [] },
  loading: false,
  conversationLoading: false,
  createLoading: false,
  error: null,
  success: false,
  messageLoading: false,
  messageError: null,
  searchQuery: '',
  selectedConversationId: null,
  nextKey: null,
  hasMore: false,
  lastFetchedAt: null,
  wsConnectionStatus: 'disconnected', // 'connected', 'reconnecting', 'disconnected'
  closeLoading: false,
};

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    fetchConversationsRequest: (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    },

    fetchConversationsSuccess: (state, action) => {
      const payload = action.payload || {};
      const conversations = payload.conversations || [];

      state.loading = false;
      state.success = true;
      state.error = null;
      state.lastFetchedAt = Date.now();
      state.conversations.byId = {};
      state.conversations.allIds = [];

      conversations.forEach((conv) => {
        if (!conv?.id) {
          console.warn('Conversation missing required id field:', conv);
          return;
        }
        state.conversations.byId[conv.id] = conv;
        state.conversations.allIds.push(conv.id);
      });

      state.nextKey = payload.nextKey || null;
      state.hasMore = Boolean(payload.nextKey);
    },

    fetchConversationsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    },

    fetchConversationRequest: (state, action) => {
      if (!action.payload?.background) {
        state.conversationLoading = true;
      }
      state.error = null;
      state.success = false;
    },

    fetchConversationSuccess: (state, action) => {
      const payload = action.payload || {};
      const conversation = payload.conversation || payload;
      
      if (!conversation?.id) {
        console.warn('Conversation missing required id field:', conversation);
        state.error = 'Invalid conversation data';
        state.conversationLoading = false;
        state.success = false;
        return;
      }

      state.currentConversation = conversation;
      state.conversationLoading = false;
      state.success = true;
      state.error = null;
      state.lastFetchedAt = Date.now();
      state.nextKey = payload.nextKey || null;
      state.hasMore = Boolean(payload.nextKey);

      state.conversations.byId[conversation.id] = conversation;
      if (!state.conversations.allIds.includes(conversation.id)) {
        state.conversations.allIds.push(conversation.id);
      }
      state.selectedConversationId = conversation.id;
    },

    fetchConversationError: (state, action) => {
      state.conversationLoading = false;
      state.error = action.payload;
      state.success = false;
      state.currentConversation = null;
    },

    setConversationsList: (state, action) => {
      const conversations = action.payload;
      state.conversations.byId = {};
      state.conversations.allIds = [];

      conversations.forEach((conv) => {
        state.conversations.byId[conv.id] = conv;
        state.conversations.allIds.push(conv.id);
      });
    },

    addMessageToConversation: (state, action) => {
      const message = action.payload;
      const conversationId = message.conversationId;
      if (!conversationId) return;

      if (state.currentConversation?.id === conversationId) {
        if (!Array.isArray(state.currentConversation.messages)) {
          state.currentConversation.messages = [];
        }
        state.currentConversation.messages = appendMessageIfNew(
          state.currentConversation.messages,
          message
        );
      }

      const isActiveChat = state.selectedConversationId === conversationId;
      bumpConversationOnNewMessage(state, conversationId, message, {
        incrementUnread: !isActiveChat,
      });
    },

    setMessageInput: (state, action) => {},

    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },

    selectConversation: (state, action) => {
      const conversationId = action.payload;
      state.selectedConversationId = conversationId;
      state.currentConversation = state.conversations.byId[conversationId] || null;
    },

    sendMessageRequest: (state) => {
      state.messageError = null;
    },

    sendMessageSuccess: (state, action) => {
      const message = action.payload;
      const conversationId = message.conversationId || state.currentConversation?.id;

      if (state.currentConversation?.id === conversationId) {
        if (!Array.isArray(state.currentConversation.messages)) {
          state.currentConversation.messages = [];
        }
        state.currentConversation.messages = appendMessageIfNew(
          state.currentConversation.messages,
          message
        );
      }

      if (conversationId) {
        bumpConversationOnNewMessage(state, conversationId, message, {
          incrementUnread: false,
        });
      }

      state.messageLoading = false;
      state.messageError = null;
    },

    sendMessageError: (state, action) => {
      state.messageLoading = false;
      state.messageError = action.payload;
    },

    createConversationRequest: (state) => {
      state.createLoading = true;
      state.error = null;
      state.success = false;
    },

    createConversationSuccess: (state, action) => {
      state.createLoading = false;
      state.success = true;
      state.error = null;
      const { conversationId } = action.payload || {};
      if (conversationId) {
        state.selectedConversationId = conversationId;
      }
    },

    createConversationError: (state, action) => {
      state.createLoading = false;
      state.error = action.payload;
      state.success = false;
    },

    closeConversationRequest: (state) => {
      state.closeLoading = true;
      state.error = null;
      state.success = false;
    },

    closeConversationSuccess: (state, action) => {
      state.closeLoading = false;
      state.success = true;
      state.error = null;
      const { conversationId } = action.payload || {};
      if (conversationId) {
        if (state.conversations.byId[conversationId]) {
          state.conversations.byId[conversationId].status = 'CLOSED';
        }
        if (state.selectedConversationId === conversationId) {
          state.selectedConversationId = null;
          state.currentConversation = null;
        }
      }
    },

    closeConversationError: (state, action) => {
      state.closeLoading = false;
      state.error = action.payload;
      state.success = false;
    },

    markConversationAsRead: (state, action) => {
      const conversationId = action.payload;
      if (state.conversations.byId[conversationId]) {
        state.conversations.byId[conversationId].unread = 0;
      }
      if (state.currentConversation?.id === conversationId) {
        state.currentConversation.unread = 0;
      }
    },

    resetConversation: (state) => {
      state.currentConversation = null;
      state.error = null;
      state.success = false;
      state.conversationLoading = false;
    },

    clearConversationState: () => initialState,

    setWsConnectionStatus: (state, action) => {
      state.wsConnectionStatus = action.payload;
    },

    updateConversation: (state, action) => {
      const { id, updates } = action.payload || {};
      if (!id || !updates) return;
      const existing = state.conversations.byId[id];
      if (existing) {
        state.conversations.byId[id] = { ...existing, ...updates };
      }
      if (state.currentConversation?.id === id) {
        state.currentConversation = { ...state.currentConversation, ...updates };
      }
    },

    upsertConversation: (state, action) => {
      const conv = action.payload;
      if (!conv || !conv.id) return;
      state.conversations.byId[conv.id] = { ...state.conversations.byId[conv.id], ...conv };
      moveConversationToTop(state, conv.id);
    },

    updateMessageStatus: (state, action) => {
      const { conversationId, messageId, status } = action.payload || {};
      if (!conversationId || !messageId) return;
      const conv = state.conversations.byId[conversationId];
      if (conv && Array.isArray(conv.messages)) {
        const msg = conv.messages.find((m) => m.id === messageId || m.messageId === messageId);
        if (msg) msg.status = status;
      }
      if (state.currentConversation?.id === conversationId && Array.isArray(state.currentConversation.messages)) {
        const msg = state.currentConversation.messages.find((m) => m.id === messageId || m.messageId === messageId);
        if (msg) msg.status = status;
      }
    },
  },
});

export const {
  fetchConversationsRequest,
  fetchConversationsSuccess,
  fetchConversationsError,
  fetchConversationRequest,
  fetchConversationSuccess,
  fetchConversationError,
  setConversationsList,
  addMessageToConversation,
  setMessageInput,
  setSearchQuery,
  selectConversation,
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageError,
  createConversationRequest,
  createConversationSuccess,
  createConversationError,
  markConversationAsRead,
  resetConversation,
  clearConversationState,
  updateConversation,
  setWsConnectionStatus,
  upsertConversation,
  updateMessageStatus,
  closeConversationRequest,
  closeConversationSuccess,
  closeConversationError,
} = conversationSlice.actions;

export const selectCurrentConversation = (state) => state.conversation?.currentConversation;
export const selectConversationsLoading = (state) => state.conversation?.loading;
export const selectConversationLoading = (state) => state.conversation?.conversationLoading;
export const selectCreateLoading = (state) => state.conversation?.createLoading;
export const selectConversationError = (state) => state.conversation?.error;
export const selectMessageLoading = (state) => state.conversation?.messageLoading;
export const selectMessageError = (state) => state.conversation?.messageError;
export const selectConversationsList = (state) => state.conversation?.conversations;
export const selectSearchQuery = (state) => state.conversation?.searchQuery;
export const selectWsConnectionStatus = (state) => state.conversation?.wsConnectionStatus;

export const selectTotalUnreadCount = (state) => {
  const conversations = state.conversation?.conversations?.byId || {};
  return Object.values(conversations).reduce((sum, conv) => sum + (conv.unread || 0), 0);
};

export default conversationSlice.reducer;
